import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Download, Printer, Share2 } from "lucide-react";
import { api } from "../lib/api";
import { cardUrl, useSeo } from "../lib/ui";
import { BrazilGap } from "../components/BrazilGap";

const usd = (n) => (n ? `$${Math.round(n).toLocaleString()}` : "—");
const ksh = (n) => (n ? `KSh ${Math.round(n).toLocaleString()}` : "—");
const int = (n) => (n || n === 0 ? Math.round(n).toLocaleString() : "—");

const Stat = ({ label, value, note, testId }) => (
  <div className="border border-zinc-800 p-4 print:border-zinc-400" data-testid={testId}>
    <p className="mono text-[0.55rem] uppercase tracking-[0.16em] text-zinc-400 print:text-zinc-600">{label}</p>
    <p className="mono mt-2 text-sm text-white print:text-black">{value}</p>
    {note ? <p className="mt-1 text-xs text-zinc-500 print:text-zinc-600">{note}</p> : null}
  </div>
);

/** One county, one page, made to print: estimated players, facilities, land, capex, opex and revenue. */
export default function CountyOnePager() {
  const { slug } = useParams();
  const shareOnWhatsApp = () => {
    const url = `${window.location.origin}/counties/${slug}/one-page`;
    const text = d
      ? `${d.county} County, costed on one page: ${int(d.players)} players reached, ${d.land_acres} acres of land, ` +
        `${usd(d.capex_usd)} to build, ${usd(d.annual_revenue_usd)} a year in potential revenue. ${url}`
      : url;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
  };
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");

  useSeo({
    title: `${d?.county || (slug ? slug.replace(/-/g, " ") : "County")} on one page — KickStartKenyaFootball`,
    description:
      "One printable page per county: estimated players reached, the facilities proposed, the land they need, capex, opex and potential revenue, with the method attached.",
    image: cardUrl("one-pager", slug),
  });

  useEffect(() => {
    api.get(`/county-one-pager/${slug}`)
      .then((r) => setD(r.data))
      .catch(() => setErr("We could not load this county."));
  }, [slug]);

  if (err)
    return (
      <div className="bg-black px-5 py-24 text-center" data-testid="one-pager-error">
        <h1 className="display-3">{err}</h1>
        <Link to="/counties" className="btn-primary mt-8 inline-flex">Back to the county map</Link>
      </div>
    );
  if (!d) return <div className="min-h-[60vh] bg-black" data-testid="one-pager-loading" />;

  return (
    <div className="bg-black pb-24 pt-10 print:bg-white print:pt-0" data-testid="county-one-pager">
      <div className="mx-auto max-w-[1000px] px-5 md:px-10 print:max-w-none print:px-0 print:text-black">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="mono text-[0.6rem] uppercase tracking-[0.2em] text-ochre">
              {d.priority ? `Priority county · rank ${d.priority_rank}` : "County research summary"}
            </p>
            <h1 className="mt-3 font-display text-3xl font-bold uppercase leading-none tracking-tight text-white print:text-black md:text-5xl"
              data-testid="one-pager-title">
              {d.county} On One Page
            </h1>
            <p className="mono mt-3 text-[0.6rem] uppercase tracking-[0.16em] text-zinc-400 print:text-zinc-600">
              Population today {int(d.population_today)} · KSh {d.kes_per_usd} to the dollar
            </p>
          </div>
          <div className="flex flex-wrap gap-3 print:hidden">
            <button type="button" onClick={() => window.print()} className="btn-primary inline-flex items-center gap-2"
              data-testid="one-pager-print">
              <Printer size={14} /> Print this page
            </button>
            <button type="button" onClick={shareOnWhatsApp} className="btn-ghost inline-flex items-center gap-2"
              data-testid="one-pager-whatsapp">
              <Share2 size={14} /> Share on WhatsApp
            </button>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/county-one-pager.pdf?county=${encodeURIComponent(slug)}`}
              className="btn-ghost inline-flex items-center gap-2" data-testid="one-pager-pdf">
              <Download size={14} /> Download the PDF
            </a>
            <Link to={`/counties/${slug}`} className="btn-ghost" data-testid="one-pager-to-dossier">
              Full county dossier
            </Link>
            <Link to={`/counties/compare-sheet?a=${encodeURIComponent(d.county)}&b=Nairobi`} className="btn-ghost"
              data-testid="one-pager-to-compare-sheet">
              Compare with another county
            </Link>
          </div>
        </div>

        <div className="mt-8 grid gap-3 sm:grid-cols-3" data-testid="one-pager-headline">
          <Stat label="Estimated players reached" value={int(d.players)}
            note={`${int(d.teams)} teams · ${int(d.coaching_places)} coaching places`} testId="one-pager-players" />
          <Stat label="Facility units proposed" value={int(d.facility_units)}
            note={`${d.facilities.length} facility types`} testId="one-pager-units" />
          <Stat label="Land needed" value={`${d.land_acres} acres`} note="Indicative take, all units"
            testId="one-pager-land" />
          <Stat label="Capex" value={usd(d.capex_usd)} note={ksh(d.capex_ksh)} testId="one-pager-capex" />
          <Stat label="Opex a year" value={usd(d.opex_usd)} note={`${d.cost_recovery_pct}% cost recovery`}
            testId="one-pager-opex" />
          <Stat label="Potential revenue a year" value={usd(d.annual_revenue_usd)}
            note={d.payback_years ? `Payback period ${d.payback_years} years` : "Needs blended funding"}
            testId="one-pager-revenue" />
        </div>

        <section className="mt-10" data-testid="one-pager-facilities">
          <h2 className="font-display text-lg uppercase tracking-tight text-ochre print:text-black md:text-xl">
            The facilities, and the land they need
          </h2>
          <div className="mt-4 overflow-x-auto border border-zinc-800 print:border-zinc-400">
            <table className="w-full min-w-[640px] text-left">
              <thead>
                <tr className="border-b border-zinc-800 bg-[#0b0b0b] print:bg-white">
                  {["Facility", "Units", "Land, acres", "Capex", "Players", "Revenue a year"].map((h) => (
                    <th key={h} className="mono px-3 py-2.5 text-[0.55rem] uppercase tracking-[0.14em] text-zinc-300 print:text-zinc-700">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {d.facilities.map((f) => (
                  <tr key={f.item} className="border-b border-zinc-900 print:border-zinc-300"
                    data-testid={`one-pager-facility-${f.item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    <td className="px-3 py-3 text-sm text-white print:text-black">{f.item}</td>
                    <td className="mono px-3 py-3 text-xs text-zinc-300 print:text-zinc-700">{int(f.units)}</td>
                    <td className="mono px-3 py-3 text-xs text-zinc-300 print:text-zinc-700">
                      {`${f.land_acres} (${f.land_acres_each} each)`}
                    </td>
                    <td className="mono px-3 py-3 text-xs text-zinc-300 print:text-zinc-700">{usd(f.capex_usd)}</td>
                    <td className="mono px-3 py-3 text-xs text-zinc-300 print:text-zinc-700">{int(f.players)}</td>
                    <td className="mono px-3 py-3 text-xs text-ochre print:text-black">{usd(f.annual_revenue_usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs leading-relaxed text-zinc-500 print:text-zinc-600">{d.land_note}</p>
          {d.facilities.some((f) => f.multi_sport) ? (
            <div className="mt-5 border border-zinc-800 p-4 print:border-zinc-400" data-testid="one-pager-multi-sport">
              <p className="mono text-[0.55rem] uppercase tracking-[0.16em] text-zinc-400 print:text-zinc-600">
                Multi-sport use, per the KPL Multi-Sports Division
              </p>
              <ul className="mt-3 space-y-2">
                {d.facilities.filter((f) => f.multi_sport).map((f) => (
                  <li key={f.item} className="text-xs leading-relaxed text-zinc-400 print:text-zinc-700">
                    <span className="text-white print:text-black">{f.item}:</span>{" "}
                    {f.multi_sport.sports.join(", ")} — {usd(f.multi_sport.extra_spec_usd)} of extra specification,{" "}
                    {usd(f.multi_sport.annual_hire_usd)} of non-football hire a year
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </section>

        <div className="mt-10">
          <BrazilGap gap={d.brazil_gap} county={d.county} testId="one-pager-brazil-gap" />
        </div>

        <section className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="one-pager-returns">
          <Stat label="Operating surplus a year" value={usd(d.operating_surplus_usd)} testId="one-pager-surplus" />
          <Stat label="Economic and social value a year" value={usd(d.annual_value_usd)}
            note={`${d.value_per_dollar} per dollar spent`} testId="one-pager-value" />
          <Stat label="Health value a year" value={usd(d.health_value_usd)} testId="one-pager-health" />
          <Stat label="Tax a year" value={usd(d.tax_usd)} testId="one-pager-tax" />
          <Stat label="Jobs" value={int(d.operational_jobs)}
            note={`${int(d.construction_job_years)} construction job-years`} testId="one-pager-jobs" />
          <Stat label="Academy places" value={int(d.academy_places)} testId="one-pager-academy" />
          <Stat label="Professionals produced a year" value={d.pros_per_year ?? "—"}
            note={d.cost_per_professional_usd ? `${usd(d.cost_per_professional_usd)} each` : ""}
            testId="one-pager-pros" />
          <Stat label="Player trading income a year" value={usd(d.programme_income_usd)} testId="one-pager-trading" />
        </section>

        <p className="mt-8 max-w-3xl text-xs leading-relaxed text-zinc-500 print:text-zinc-600"
          data-testid="one-pager-note">
          {d.note}
        </p>
        <div className="mono mt-6 flex flex-wrap gap-4 text-[0.6rem] uppercase tracking-[0.16em] print:hidden">
          <Link to="/sources" className="text-ochre hover:text-white" data-testid="one-pager-to-sources">Sources and method</Link>
          <Link to="/site-appraisal" className="text-ochre hover:text-white" data-testid="one-pager-to-appraisal">Appraise a plot here</Link>
          <Link to="/apply" className="text-ochre hover:text-white" data-testid="one-pager-to-apply">Send us a site</Link>
        </div>
      </div>
    </div>
  );
}
